import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './PasswordStrengthMeter.css';

const PasswordStrengthMeter = ({ password }) => {
  // Criterios de fortaleza
  const criteria = [
    { id: 'length', label: 'Al menos 8 caracteres', test: pwd => pwd.length >= 8 },
    { id: 'lowercase', label: 'Al menos una letra minúscula', test: pwd => /[a-z]/.test(pwd) },
    { id: 'uppercase', label: 'Al menos una letra mayúscula', test: pwd => /[A-Z]/.test(pwd) },
    { id: 'number', label: 'Al menos un número', test: pwd => /[0-9]/.test(pwd) },
    { id: 'special', label: 'Al menos un carácter especial', test: pwd => /[^A-Za-z0-9]/.test(pwd) },
  ];

  // Animaciones del Design System
  const containerVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.23, 1, 0.32, 1],
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      x: -20,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      x: 0,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }
    }
  };

  const progressVariants = {
    hidden: { width: "0%" },
    visible: (percentage) => ({
      width: `${percentage}%`,
      transition: {
        duration: 0.6,
        ease: [0.23, 1, 0.32, 1]
      }
    })
  };

  const checkmarkVariants = {
    hidden: { 
      scale: 0,
      rotate: -180
    },
    visible: { 
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.4,
        ease: [0.23, 1, 0.32, 1]
      }
    }
  };

  // Calcular puntuación
  const calculateScore = (pwd) => {
    if (!pwd) return 0;
    return criteria.filter(criterion => criterion.test(pwd)).length;
  };

  const score = calculateScore(password);
  const maxScore = criteria.length;
  
  // Determinar nivel de fortaleza
  let strengthClass = 'danger';
  let strengthText = 'Muy débil';
  
  if (score === maxScore) {
    strengthClass = 'success';
    strengthText = 'Fuerte';
  } else if (score >= maxScore - 1) {
    strengthClass = 'warning';
    strengthText = 'Moderada';
  } else if (score >= maxScore - 2) {
    strengthClass = 'warning';
    strengthText = 'Débil';
  }

  // Calcular porcentaje para la barra de progreso
  const percentage = (score / maxScore) * 100;

  return (
    <AnimatePresence>
      {password && (
        <motion.div 
          className="password-strength-meter"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
        >
          <motion.div 
            className="progress mb-2"
            variants={itemVariants}
          >
            <motion.div 
              className={`progress-bar bg-${strengthClass}`} 
              role="progressbar" 
              variants={progressVariants}
              custom={percentage}
              aria-valuenow={percentage} 
              aria-valuemin="0" 
              aria-valuemax="100"
            />
          </motion.div>
          
          <motion.div 
            className="strength-text text-muted small mb-2"
            variants={itemVariants}
          >
            Fortaleza de contraseña: {' '}
            <motion.span 
              className={`text-${strengthClass}`}
              key={strengthText}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
            >
              {strengthText}
            </motion.span>
          </motion.div>
          
          <motion.div 
            className="criteria-list"
            variants={itemVariants}
          >
            <ul className="list-unstyled small">
              {criteria.map((criterion, index) => {
                const isValid = password && criterion.test(password);
                return (
                  <motion.li 
                    key={criterion.id} 
                    className={isValid ? 'text-success' : 'text-muted'}
                    variants={itemVariants}
                    whileHover={{ 
                      x: 5,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <AnimatePresence mode="wait">
                      {isValid ? (
                        <motion.i 
                          className="bi bi-check-circle-fill me-1"
                          variants={checkmarkVariants}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          key="check"
                        />
                      ) : (
                        <motion.i 
                          className="bi bi-circle me-1"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ duration: 0.2 }}
                          key="circle"
                        />
                      )}
                    </AnimatePresence>
                    <motion.span
                      initial={{ opacity: 0.7 }}
                      animate={{ 
                        opacity: isValid ? 1 : 0.7,
                        fontWeight: isValid ? 600 : 500
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      {criterion.label}
                    </motion.span>
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PasswordStrengthMeter; 